import type { WearablePathTrack } from "./wearables.pathDefaults";

export type WearablesTimeOfDay = "morning" | "afternoon" | "evening" | "night";
export type WearablesSessionType = "deep" | "light" | "xr" | "focused" | "recovery";

export type WearablesBehaviorMemory = {
  preferredModes: Partial<Record<WearablePathTrack, number>>;
  sessionTypeCounts: Partial<Record<WearablesSessionType, number>>;
  timeOfDayCounts: Partial<Record<WearablesTimeOfDay, number>>;
  lastAcceptedMode?: WearablePathTrack;
  overrideCount: number;
  ignoredCount: number;
  acceptanceRate: number;
  totalRecommendations: number;
  totalAcceptances: number;
  totalChoices: number;
  updatedAt: string;
};

const KEY = "wearables.behavior.v3";

function getStorage(): Storage | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }

  return window.localStorage;
}

function createDefaultMemory(): WearablesBehaviorMemory {
  return {
    preferredModes: {},
    sessionTypeCounts: {},
    timeOfDayCounts: {},
    overrideCount: 0,
    ignoredCount: 0,
    acceptanceRate: 0,
    totalRecommendations: 0,
    totalAcceptances: 0,
    totalChoices: 0,
    updatedAt: new Date(0).toISOString(),
  };
}

function writeMemory(next: WearablesBehaviorMemory): WearablesBehaviorMemory {
  const storage = getStorage();

  if (!storage) {
    return next;
  }

  storage.setItem(KEY, JSON.stringify(next));
  return next;
}

export function getBehaviorMemory(): WearablesBehaviorMemory {
  const storage = getStorage();
  const fallback = createDefaultMemory();

  if (!storage) {
    return fallback;
  }

  try {
    const raw = storage.getItem(KEY);
    if (!raw) {
      return fallback;
    }

    const parsed = JSON.parse(raw) as Partial<WearablesBehaviorMemory>;
    return {
      ...fallback,
      ...parsed,
      preferredModes: parsed.preferredModes ?? {},
      sessionTypeCounts: parsed.sessionTypeCounts ?? {},
      timeOfDayCounts: parsed.timeOfDayCounts ?? {},
    };
  } catch {
    return fallback;
  }
}

function updateMemory(
  updater: (current: WearablesBehaviorMemory) => WearablesBehaviorMemory,
): WearablesBehaviorMemory {
  const current = getBehaviorMemory();
  return writeMemory(updater(current));
}

export function recordRecommendationExposure(): WearablesBehaviorMemory {
  return updateMemory((current) => {
    const totalRecommendations = current.totalRecommendations + 1;

    return {
      ...current,
      totalRecommendations,
      acceptanceRate:
        totalRecommendations > 0 ? current.totalAcceptances / totalRecommendations : 0,
      updatedAt: new Date().toISOString(),
    };
  });
}

export function recordUserChoice(args: {
  mode: WearablePathTrack;
  timeOfDay?: WearablesTimeOfDay;
  sessionType?: WearablesSessionType;
}): WearablesBehaviorMemory {
  return updateMemory((current) => {
    const totalAcceptances = current.totalAcceptances + 1;
    const totalRecommendations = Math.max(current.totalRecommendations, totalAcceptances);

    return {
      ...current,
      preferredModes: {
        ...current.preferredModes,
        [args.mode]: (current.preferredModes[args.mode] ?? 0) + 1,
      },
      sessionTypeCounts: args.sessionType
        ? {
            ...current.sessionTypeCounts,
            [args.sessionType]: (current.sessionTypeCounts[args.sessionType] ?? 0) + 1,
          }
        : current.sessionTypeCounts,
      timeOfDayCounts: args.timeOfDay
        ? {
            ...current.timeOfDayCounts,
            [args.timeOfDay]: (current.timeOfDayCounts[args.timeOfDay] ?? 0) + 1,
          }
        : current.timeOfDayCounts,
      lastAcceptedMode: args.mode,
      totalAcceptances,
      totalRecommendations,
      totalChoices: current.totalChoices + 1,
      acceptanceRate: totalRecommendations > 0 ? totalAcceptances / totalRecommendations : 0,
      updatedAt: new Date().toISOString(),
    };
  });
}

export function recordRecommendationOverride(): WearablesBehaviorMemory {
  return updateMemory((current) => ({
    ...current,
    overrideCount: current.overrideCount + 1,
    updatedAt: new Date().toISOString(),
  }));
}

export function recordIgnoredRecommendation(): WearablesBehaviorMemory {
  return updateMemory((current) => ({
    ...current,
    ignoredCount: current.ignoredCount + 1,
    updatedAt: new Date().toISOString(),
  }));
}

export function getPreferredModeWeight(mode: WearablePathTrack): number {
  const memory = getBehaviorMemory();
  const count = memory.preferredModes[mode] ?? 0;

  if (count === 0 || memory.totalChoices === 0) {
    return 0;
  }

  return Math.min(count / memory.totalChoices, 1);
}